import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle2, Download, Home } from "lucide-react";
import MaintenanceOverlay from "@/components/MaintenanceOverlay";

function ConfirmationPageContent() {
  const { token } = useParams();
  const confirmationNumber = `SAG-${token.substring(0, 8).toUpperCase()}-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}`;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      <div className="max-w-lg w-full animate-fade-in">
        <Card className="border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-[#003366] font-['Manrope']">
              Assessment Submitted Successfully!
            </CardTitle>
            <CardDescription className="text-base mt-2">
              Thank you for contributing to Sagicor's technology transformation.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Confirmation Number */}
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                Confirmation Number
              </p>
              <p className="text-lg font-mono font-bold text-[#003366]" data-testid="confirmation-number">
                {confirmationNumber}
              </p>
            </div>

            {/* Info Box */}
            <div className="bg-[#003366]/5 rounded-lg p-4 border border-[#003366]/10">
              <h4 className="font-semibold text-[#003366] mb-2">What happens next?</h4>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 bg-[#B8860B] text-white rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">1</span>
                  <span>Your responses will be reviewed by the Technology Capability Assessment team.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 bg-[#B8860B] text-white rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">2</span>
                  <span>Your manager will receive a summary to support your development planning.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 bg-[#B8860B] text-white rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">3</span>
                  <span>You may be contacted for follow-up discussions about your career aspirations.</span>
                </li>
              </ul>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-3 pt-4">
              <Link to="/">
                <Button 
                  className="w-full h-12 bg-[#003366] hover:bg-[#003366]/90 gap-2"
                  data-testid="return-home-btn"
                >
                  <Home className="w-4 h-4" />
                  Return to Home
                </Button>
              </Link>
            </div>

            {/* Footer */}
            <p className="text-xs text-center text-muted-foreground pt-4 border-t">
              A confirmation email has been sent to your registered email address.
              Please save your confirmation number for future reference.
            </p>
          </CardContent>
        </Card>

        {/* Sagicor Branding */}
        <div className="flex items-center justify-center gap-2 mt-8">
          <div className="w-8 h-8 bg-[#003366] rounded flex items-center justify-center">
            <span className="text-white font-bold text-sm">S</span>
          </div>
          <span className="text-sm text-muted-foreground">
            Sagicor Technology Assessment Platform
          </span>
        </div>
      </div>
    </div>
  );
}

// Export wrapped with MaintenanceOverlay
export default function ConfirmationPage() {
  return (
    <MaintenanceOverlay>
      <ConfirmationPageContent />
    </MaintenanceOverlay>
  );
}
